import {
  LockOutlined,
  MailOutlined,
  UserOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { Button, Card, Col, Form, Input, Row } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom"; // Assuming you're using react-router for navigation
import logo from "../graphics/logo.svg"; // Adjust the import path based on your project structure
import { setAuthToken } from "../helpers/userDetails";
import useMultiNotification from "../hooks/useNotification";
import { signupApi } from "../redux-toolkit/slices/authSlice";
import { RootState, useAppDispatch } from "../redux-toolkit/store/store";
const SignupCream = () => {
  const [searchParams] = useSearchParams();
  const redirectToPayment = searchParams.get("redirectToPayment");
  const dispatch = useAppDispatch();
  const authState = useSelector((state: RootState) => state.auth);
  const { contextHolder, openNotification } = useMultiNotification();
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const onFinish = async (values: ISignupApi) => {
    try {
      const user = await dispatch(signupApi(values)).unwrap();
      const token = user.responseData;
      setAuthToken(token);
      if (redirectToPayment) {
        window.location.href = "/payment";
        return;
      } else {
        window.location.href = "/";
      }
    } catch (error) {
      if (typeof error === "string") {
        openNotification(`error`, `Sign Up Failed`, error);
      } else {
        openNotification(`error`, `Sign Up Failed`, `Server Error`);
      }
    }
  };
  const onFinishFailed = (data: any) => {};
  return (
    <>
      {contextHolder}
      <div className="common-section">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center py-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src={logo}
                alt="Twinge Health Logo"
                className="w-[180px] md:w-[220px] lg:w-[250px] h-auto"
              />
            </div>

            {/* Signup Form Container */}
            <div
              className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden relative w-full max-w-4xl"
              style={{
                backgroundColor: "var(--neutral-100)",
                maxWidth: "900px",
              }}
            >
              <h3 className="h3 mb-6 w-full" style={{ textAlign: "left" }}>
                Yay, we operate in your state! Your pain relief journey starts
                here!
              </h3>

              {/* Description Text */}
              <div style={{ textAlign: "left" }}>
                <p className="body1 text-center mb-2">
                  Here's what's next after you sign up:
                </p>
                <p className="body1 text-center mb-2">
                  <IconCircleCheckFilled size={15} /> Review the details of your
                  order and payment information.
                </p>
                <p className="body1 text-center mb-2">
                  <IconCircleCheckFilled size={15} /> Do your telehealth
                  assessment. (15 minutes)
                </p>
                <p className="body1 text-center mb-2">
                  <IconCircleCheckFilled size={15} /> Receive your pain cream in
                  the mail in about 3-4 days after your assessment.
                </p>
                <p className="body1 text-center mb-2">
                  Behind the scenes a physician prescribes the formula based on
                  your assessment that is made custom for you per doctor's
                  prescription. Then it is shipped to you ready to provide
                  relief.
                </p>
              </div>

              {/* Wavy Line Separator */}
              <div className="flex justify-left mb-6">
                <svg
                  viewBox="0 0 122 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[92px] md:w-[122px]"
                >
                  <path
                    opacity="0.4"
                    d="M1.46484 6.83613L4.45387 3.7103C7.74598 0.267505 13.38 0.760513 16.0241 4.72277L16.5428 5.50001C19.2423 9.54539 25.1877 9.54539 27.8873 5.5V5.5C30.5869 1.45461 36.5322 1.45461 39.2318 5.5V5.5C41.9314 9.54539 47.8768 9.54539 50.5764 5.5V5.5C53.2759 1.45461 59.2213 1.45461 61.9209 5.5V5.5C64.6205 9.54539 70.5658 9.54539 73.2654 5.5V5.5C75.965 1.45461 81.9104 1.45461 84.61 5.5V5.5C87.3096 9.54539 93.2549 9.54539 95.9545 5.5V5.5C98.6541 1.45461 104.599 1.45461 107.299 5.5V5.5C109.999 9.54539 115.944 9.54539 118.644 5.5L120.534 2.66667"
                    stroke="var(--primary-500)"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <Form
                name="signUpForm"
                initialValues={{ remember: true }}
                layout="vertical"
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                className="w-full flex flex-col items-left"
              >
                <div className="flex flex-col gap-3 w-full">
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="firstName"
                        rules={[
                          {
                            required: true,
                            message: "What is your first name?",
                          },
                        ]}
                        label={
                          <span className="h6 text-theme-text-secondary">
                            First Name
                          </span>
                        }
                      >
                        <Input placeholder="First Name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="lastName"
                        rules={[
                          {
                            required: true,
                            message: "What is your last name?",
                          },
                        ]}
                        label={
                          <span className="h6 text-theme-text-secondary">
                            Last Name
                          </span>
                        }
                      >
                        <Input placeholder="Last Name" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Please input your email!" },
                      {
                        type: "email",
                        message: "Please enter a valid email address!",
                      },
                      {
                        pattern:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message:
                          "Please enter a valid email with domain (e.g., your@domain.com)",
                      },
                    ]}
                    label={
                      <span className="h6 text-theme-text-secondary">
                        Email Address
                      </span>
                    }
                  >
                    <Input placeholder="your@email.com" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: "Please input your password!",
                          },
                        ]}
                        label={
                          <span className="h6 text-theme-text-secondary">
                            Password
                          </span>
                        }
                      >
                        <div style={{ position: "relative" }}>
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            style={{ paddingRight: "40px" }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "var(--neutral-500)",
                              padding: "0",
                              zIndex: 10,
                            }}
                          >
                            {showPassword ? (
                              <EyeOutlined />
                            ) : (
                              <EyeInvisibleOutlined />
                            )}
                          </button>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="confirm-password"
                        dependencies={["password"]} // This tells the form to track the password field
                        rules={[
                          {
                            required: true,
                            message: "Please confirm your password!",
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                !value ||
                                getFieldValue("password") === value
                              ) {
                                return Promise.resolve(); // Passwords match
                              }
                              return Promise.reject(
                                new Error("The two passwords do not match!")
                              );
                            },
                          }),
                        ]}
                        label={
                          <span className="h6 text-theme-text-secondary">
                            Confirm Password
                          </span>
                        }
                      >
                        <div style={{ position: "relative" }}>
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            style={{ paddingRight: "40px" }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "var(--neutral-500)",
                              padding: "0",
                              zIndex: 10,
                            }}
                          >
                            {showConfirmPassword ? (
                              <EyeOutlined />
                            ) : (
                              <EyeInvisibleOutlined />
                            )}
                          </button>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Sign Up Button */}
                  <div className="flex justify-center gap-4">
                    <button
                      type="submit"
                      disabled={authState.isSubmitting}
                      className="btn btn-primary btn-lg"
                    >
                      {authState.isSubmitting
                        ? "Creating Account..."
                        : "Sign Up"}
                    </button>
                  </div>
                </div>
              </Form>
            </div>

            {/* Sign In Link */}
            <div style={{ marginTop: "32px", textAlign: "center" }}>
              <p className="body1 text-sm text-theme-text-secondary">
                Already have an account?
                <Link
                  to="/login"
                  className="caption2 font-medium transition-colors duration-200"
                  style={{
                    color: "var(--primary-500)",
                    textDecoration: "none",
                  }}
                >
                  &nbsp;Log in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupCream;
